import { useApolloClient, useMutation } from "@apollo/client/react";
import { type ApolloCache } from "@apollo/client/cache";
import {
  AddContactsDocument,
  RemoveContactsDocument,
} from "@/graphql/generated/operations";

export interface ContactAddInput {
  contactIds: string[];
  groups?: string[];
  removeContactsFromOtherGroups?: boolean;
}

export interface ContactRemoveInput {
  contactIds: string[];
}

export interface UseContactResult {
  add: (input: ContactAddInput) => Promise<void>;
  remove: (input: ContactRemoveInput) => Promise<void>;
}

function flipIsContact(
  cache: ApolloCache,
  contactIds: string[],
  next: boolean,
) {
  for (const id of contactIds) {
    cache.modify({
      id: cache.identify({ __typename: "Person", id }),
      fields: { isContact: () => next },
    });
  }
}

export function useContact(): UseContactResult {
  const [addMut] = useMutation(AddContactsDocument);
  const [removeMut] = useMutation(RemoveContactsDocument);
  const client = useApolloClient();

  async function add(input: ContactAddInput) {
    const res = await addMut({
      variables: {
        input: {
          contacts: input.contactIds,
          groups: input.groups,
          removeContactsFromOtherGroups: input.removeContactsFromOtherGroups,
        },
      },
    });
    const out = res.data?.addContacts;
    if (out?.__typename !== "AddContactsResult") {
      throw new Error(
        out?.__typename === "DailyQuotaExceeded"
          ? "DailyQuotaExceeded"
          : "AddContacts rejected",
      );
    }
    flipIsContact(client.cache, input.contactIds, true);
    if (input.groups !== undefined) {
      await client.refetchQueries({ include: ["PersonGroupMemberships"] });
    }
  }

  async function remove(input: ContactRemoveInput) {
    const res = await removeMut({
      variables: { input: { contacts: input.contactIds } },
    });
    const out = res.data?.removeContacts;
    if (out?.__typename !== "RemoveContactsResult") {
      throw new Error("RemoveContacts rejected");
    }
    flipIsContact(client.cache, input.contactIds, false);
  }

  return { add, remove };
}
